import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <Helmet>
        <title>Datenschutzerklärung | Rieppel Bäckerei GmbH – Cafe Konditorei Rieppel Wien</title>
        <meta name="description" content="Datenschutzerklärung der Rieppel Bäckerei GmbH. Informationen zum Schutz Ihrer personenbezogenen Daten." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://rieppel-baeckerei.at/datenschutz" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://rieppel-baeckerei.at/" },
            { "@type": "ListItem", "position": 2, "name": "Datenschutz" }
          ]
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#1e1919] text-white py-4 sm:py-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Zurück zur Startseite</span>
          </Link>
          <h1 className="font-bold text-lg sm:text-xl">Datenschutz</h1>
          <div className="w-20 sm:w-32"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-3">Datenschutzerklärung</h1>
        <p className="text-[#736c64] text-sm mb-8 sm:mb-10">Stand: 4. April 2026</p>

        <div className="space-y-8 sm:space-y-10">
          {/* Verantwortlicher */}
          <section data-testid="datenschutz-responsible">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">1. Verantwortlicher</h2>
            <div className="space-y-1 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p className="font-semibold text-[#1e1919]">Rieppel Bäckerei GmbH</p>
              <p>Märzstraße 71/1-7, 1150 Wien</p>
              <p>Telefon: <a href="tel:+4319825285" className="text-[#61525a] hover:text-[#1e1919] transition-colors">01 9825285</a></p>
              <p>E-Mail: <a href="mailto:yukselercifci73@gmail.com" className="text-[#61525a] hover:text-[#1e1919] transition-colors">yukselercifci73@gmail.com</a></p>
            </div>
          </section>

          {/* Grundsätze */}
          <section data-testid="datenschutz-principles">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">2. Grundsätze der Datenverarbeitung</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. Wir verarbeiten Ihre Daten daher 
                ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, TKG 2003, DSG). In dieser 
                Datenschutzerklärung informieren wir Sie über die wichtigsten Aspekte der Datenverarbeitung im Rahmen 
                unserer Website.
              </p>
              <p>
                Personenbezogene Daten werden von uns nur im rechtlich zulässigen Rahmen verarbeitet. Wir halten uns 
                an die Grundsätze der Datenverarbeitung gemäß Art. 5 DSGVO: Rechtmäßigkeit, Verarbeitung nach Treu und 
                Glauben, Transparenz, Zweckbindung, Datenminimierung, Richtigkeit, Speicherbegrenzung sowie Integrität 
                und Vertraulichkeit.
              </p>
            </div>
          </section>

          {/* Website-Besuch */}
          <section data-testid="datenschutz-website">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">3. Zugriffsdaten / Server-Logfiles</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Beim Besuch unserer Website werden durch den Webserver automatisch sogenannte Server-Logfiles erfasst. 
                Diese enthalten folgende Informationen:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Browsertyp und Browserversion</li>
                <li>Verwendetes Betriebssystem</li>
                <li>Referrer URL (die zuvor besuchte Seite)</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Datum und Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse</li>
              </ul>
              <p>
                Diese Daten sind nicht bestimmten Personen zuordenbar. Eine Zusammenführung dieser Daten mit anderen 
                Datenquellen wird nicht vorgenommen. Die Daten werden nach einer statistischen Auswertung gelöscht. 
                Die Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).
              </p>
            </div>
          </section>

          {/* Kontaktaufnahme */}
          <section data-testid="datenschutz-contact">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">4. Kontaktaufnahme</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Wenn Sie per Telefon oder E-Mail Kontakt mit uns aufnehmen, werden die von Ihnen mitgeteilten 
                Daten (Name, Telefonnummer, E-Mail-Adresse) von uns gespeichert, um Ihre Anfrage zu bearbeiten. 
                Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung bzw. vorvertragliche Maßnahmen).
              </p>
              <p>
                Die in diesem Zusammenhang anfallenden Daten löschen wir, nachdem die Speicherung nicht mehr 
                erforderlich ist, oder schränken die Verarbeitung ein, falls gesetzliche Aufbewahrungspflichten bestehen.
              </p>
            </div>
          </section>

          {/* Google Maps */}
          <section data-testid="datenschutz-maps">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">5. Google Maps</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Diese Website nutzt Google Maps zur Darstellung unseres Standortes. Anbieter ist die Google Ireland 
                Limited, Gordon House, Barrow Street, Dublin 4, Irland.
              </p>
              <p>
                Beim Aufruf der Seite mit eingebetteter Karte wird eine Verbindung zu den Servern von Google hergestellt. 
                Dabei wird Ihre IP-Adresse an Google übermittelt. Google kann unter Umständen Cookies in Ihrem Browser 
                setzen. Weitere Informationen finden Sie in der{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#61525a] hover:text-[#1e1919] underline transition-colors">
                  Datenschutzerklärung von Google
                </a>.
              </p>
              <p>
                Die Nutzung von Google Maps erfolgt im Interesse einer ansprechenden Darstellung unseres Standortes. 
                Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section data-testid="datenschutz-cookies">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">6. Cookies</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Unsere Website verwendet keine eigenen Cookies zur Nachverfolgung oder Analyse. Durch die Einbindung 
                externer Dienste (z.B. Google Maps) können jedoch Cookies von Drittanbietern gesetzt werden. 
                Sie können die Speicherung von Cookies in Ihren Browsereinstellungen verhindern; dies kann jedoch 
                die Funktionalität der Website einschränken.
              </p>
            </div>
          </section>

          {/* Rechte */}
          <section data-testid="datenschutz-rights">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">7. Ihre Rechte</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Ihnen stehen gemäß DSGVO grundsätzlich die folgenden Rechte zu:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-[#1e1919]">Auskunftsrecht</strong> (Art. 15 DSGVO)</li>
                <li><strong className="text-[#1e1919]">Berichtigungsrecht</strong> (Art. 16 DSGVO)</li>
                <li><strong className="text-[#1e1919]">Löschungsrecht</strong> (Art. 17 DSGVO)</li>
                <li><strong className="text-[#1e1919]">Recht auf Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
                <li><strong className="text-[#1e1919]">Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
                <li><strong className="text-[#1e1919]">Widerspruchsrecht</strong> (Art. 21 DSGVO)</li>
              </ul>
              <p>
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:{' '}
                <a href="mailto:yukselercifci73@gmail.com" className="text-[#61525a] hover:text-[#1e1919] underline transition-colors">
                  yukselercifci73@gmail.com
                </a>
              </p>
            </div>
          </section>

          {/* Beschwerde */}
          <section data-testid="datenschutz-complaint">
            <h2 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 border-b border-[#e5e2df] pb-2">8. Beschwerderecht</h2>
            <div className="space-y-3 text-[#736c64] text-sm sm:text-base leading-relaxed">
              <p>
                Sie haben das Recht, sich bei der zuständigen Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, 
                dass die Verarbeitung Ihrer personenbezogenen Daten gegen die DSGVO verstößt. Die zuständige 
                Aufsichtsbehörde in Österreich ist:
              </p>
              <div className="bg-white rounded-xl p-4 sm:p-5 mt-2">
                <p className="font-semibold text-[#1e1919]">Österreichische Datenschutzbehörde</p>
                <p>Barichgasse 40-42, 1030 Wien</p>
                <p>Telefon: +43 1 52 152-0</p>
                <p>
                  E-Mail:{' '}
                  <a href="mailto:dsb@dsb.gv.at" className="text-[#61525a] hover:text-[#1e1919] underline transition-colors">
                    dsb@dsb.gv.at
                  </a>
                </p>
                <p>
                  Website:{' '}
                  <a href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer" className="text-[#61525a] hover:text-[#1e1919] underline transition-colors">
                    www.dsb.gv.at
                  </a>
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-[#e5e2df]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#61525a] hover:text-[#1e1919] transition-colors font-medium"
            data-testid="datenschutz-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>
        </div>
      </main>
    </div>
  );
};

export default DatenschutzPage;
